/**
 * Canje del handoff next → app actual (#1525).
 *
 * El handoff era de una sola dirección (legacy → next, #1006): esta app minteaba el
 * token pero nunca lo consumía, así que un usuario que vivía en next y saltaba acá
 * (Shopify connect, Cambiar plan, CTA de suscripción vencida) caía en el login.
 *
 * Corre ANTES de montar React, a propósito: `UserContext.initializeUser` lee
 * localStorage en un `useEffect`, y los efectos de los hijos disparan antes que el
 * del provider — si canjeáramos ahí, alguna llamada saldría sin token, el
 * interceptor de `apiClient` vería el 401 y expulsaría al login en medio de un
 * handoff válido. Dejando los tokens en localStorage antes del render, el arranque
 * normal se encuentra la sesión ya puesta y no hay caso especial que coordinar.
 *
 * El token viaja en el FRAGMENTO (no en la query): no llega al servidor ni al
 * `Referer`. Es de un solo uso y expira en 45s (Redis, backend).
 */
import axios from 'axios';

import { backend_url as backendUrl } from '../config/variables';

/** Claves que espera el resto de la app. Espeja `UserContext.login` y `AUTH_KEYS` de `api/apiClient`. */
const TOKEN_KEY = 'token';
const REFRESH_KEY = 'refreshToken';
const SUCCESS_KEY = 'successStatus';

export function readHandoffFromHash() {
  try {
    const hash = window.location.hash;
    if (!hash || hash.indexOf('handoff=') === -1) return null;
    return new URLSearchParams(hash.slice(1)).get('handoff');
  } catch {
    return null;
  }
}

/** Borra el fragmento sin recargar ni dejar el token en el historial. */
function stripHash() {
  try {
    window.history.replaceState(
      {},
      document.title,
      window.location.pathname + window.location.search,
    );
  } catch {
    /* history bloqueado: el token igual ya se consumió (single-use) */
  }
}

/**
 * Si la URL trae `#handoff=<token>`, lo canjea y deja la sesión en localStorage.
 * Devuelve `true` solo si quedó sesión nueva.
 *
 * Un handoff inválido/expirado NO toca la sesión existente: se limpia el fragmento y
 * el arranque normal sigue con lo que hubiera (espejo del `AuthContext` de next).
 */
export async function consumeHandoffFromHash() {
  const token = readHandoffFromHash();
  if (!token) return false;
  try {
    const res = await axios.post(`${backendUrl}/auth/handoff/exchange`, {
      handoff_token: token,
    });
    const { access_token: access, refresh_token: refresh } = res.data ?? {};
    if (access && refresh) {
      localStorage.setItem(TOKEN_KEY, access);
      localStorage.setItem(REFRESH_KEY, refresh);
      localStorage.setItem(SUCCESS_KEY, 'true');
      stripHash();
      return true;
    }
  } catch {
    /* invalido / expirado / ya usado / backend caido → se ignora */
  }
  stripHash();
  return false;
}
